import React, { useState, useEffect } from 'react';
import base_url from './api/Config';
import { Circles } from 'react-loader-spinner';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import getApplicationId from './api/getApplicationId';
import TopBar from './component/TopBar';
import authFetch from './api/authFetch';

const statusMap = {
  1:  { label: 'Registered',          path: '/Address'          },
  2:  { label: 'Address Complete',     path: '/CreditCheck'      },
  3:  { label: 'CIBIL Complete',       path: '/BankDetail'       },
  4:  { label: 'Banking Complete',     path: '/Employmentdetails'},
  5:  { label: 'Employment Complete',  path: '/Nominee'          },
  6:  { label: 'Nominee Complete',     path: '/Reference'        },
  7:  { label: 'Reference Complete',   path: '/DocumentUpload'   },
  8:  { label: 'Documents Uploaded',   path: '/ProductTypes'     },
  9:  { label: 'Loan Under Process',   path: null                },
  10: { label: 'Loan Disbursed',       path: '/CustomerLoanList' },
  15: { label: 'Loan Rejected',        path: null                },
};

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processModal, setProcessModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setIsLoading(true);
        const fintechId = localStorage.getItem('fintechId');
        if (!fintechId || fintechId === 'null') {
          navigate('/Home');
          return;
        }
        const res = await authFetch(`${base_url}/api/customer/fintechIdByCustomerList?fintechId=${fintechId}`);
        if (!res.ok) throw new Error('Failed to fetch customer list');
        const data = await res.json();

        const withStatus = await Promise.all(data.map(async (c) => {
          try {
            const statusRes = await authFetch(`${base_url}/api/lead/customerStatus?customerId=${c.id}`);
            const { customerStatus } = await statusRes.json();
            const code = parseInt(customerStatus || 1);
            const info = statusMap[code] || { label: 'Unknown', path: '/Register' };
            return { ...c, statusCode: code, statusLabel: info.label };
          } catch {
            return { ...c, statusCode: 0, statusLabel: 'Unknown' };
          }
        }));

        const sorted = withStatus.sort((a, b) => b.id - a.id);
        setCustomers(sorted);
        setFilteredCustomers(sorted);
      } catch (err) {
        setError(err.message || 'Something went wrong');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  useEffect(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) { setFilteredCustomers(customers); return; }
    setFilteredCustomers(
      customers.filter(c =>
        (c.borrowerName || '').toLowerCase().includes(q) ||
        (c.mobileNumber || '').includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        String(c.id || '').includes(q) ||
        String(c.applicationId || '').includes(q)
      )
    );
  }, [searchQuery, customers]);

  const handleCustomerClick = async (customer) => {
    try {
      const res = await authFetch(`${base_url}/api/lead/customerStatus?customerId=${customer.id}`);
      if (!res.ok) throw new Error();
      const { customerStatus } = await res.json();
      const code = parseInt(customerStatus || 1);

      if (code === 9)  { setProcessModal(true); return; }
      if (code === 15) { setRejectModal(true); return; }
      if (code === 10) { navigate(`/CustomerLoanList?customerId=${customer.id}`); return; }

      const info = statusMap[code] || { path: '/Register' };
      const applicationId = await getApplicationId(customer.id);
      navigate(`${info.path}?applicationId=${applicationId}&customerId=${customer.id}`);
    } catch {
      alert('Could not track customer status.');
    }
  };

  const statusColor = (code) => {
    if (code === 10) return '#11c232';
    if (code === 15) return '#e53935';
    if (code === 9)  return '#f57c00';
    return '#1d2e77';
  };

  return (
    <div className="app-page">
      <TopBar title="Customer List" backPath="/Home" />

      <div className="app-content">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            className="field-input"
            type="text"
            placeholder="Search name, mobile, email, ID..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ flex: 1, marginBottom: 0 }}
          />
          <button
            className="btn-primary-app"
            style={{ width: 'auto', padding: '0 16px', height: 44, marginBottom: 0 }}
            onClick={() => navigate('/Register')}
          >
            + Add
          </button>
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Circles height={60} width={60} color="#1d2e77" />
          </div>
        ) : error ? (
          <div className="app-card" style={{ color: '#e53935', textAlign: 'center' }}>{error}</div>
        ) : filteredCustomers.length === 0 ? (
          <div className="app-card" style={{ textAlign: 'center', color: '#888' }}>No customers found.</div>
        ) : (
          filteredCustomers.map((customer, i) => (
            <div
              key={i}
              className="app-card"
              style={{ cursor: 'pointer', marginBottom: 10 }}
              onClick={() => handleCustomerClick(customer)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <p style={{ fontWeight: 700, fontSize: 15, color: '#1d2e77', margin: 0 }}>
                  {customer.borrowerName}
                </p>
                <span
                  className="status-badge"
                  style={{ background: statusColor(customer.statusCode) + '18', color: statusColor(customer.statusCode) }}
                >
                  {customer.statusLabel}
                </span>
              </div>
              <div className="info-row" style={{ borderBottom: 'none', paddingBottom: 4 }}>
                <span>Mobile</span>
                <span>{customer.mobileNumber}</span>
              </div>
              <div className="info-row" style={{ borderBottom: 'none', paddingBottom: 4 }}>
                <span>Email</span>
                <span style={{ fontSize: 12 }}>{customer.email}</span>
              </div>
              <div className="info-row" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <span>Customer ID</span>
                <span>{customer.id}</span>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal show={processModal} onHide={() => setProcessModal(false)} centered>
        <Modal.Body className="text-center p-4">
          <div style={{ fontSize: 44, marginBottom: 10 }}>⏳</div>
          <p style={{ fontWeight: 700, fontSize: 16, color: '#f57c00', marginBottom: 8 }}>Loan Under Process</p>
          <p style={{ fontSize: 14, color: '#555' }}>
            Your loan is currently under review. Our team will verify and notify you soon whether it is approved or rejected.
          </p>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <button className="btn btn-warning" style={{ color: '#fff' }} onClick={() => setProcessModal(false)}>OK</button>
        </Modal.Footer>
      </Modal>

      <Modal show={rejectModal} onHide={() => setRejectModal(false)} centered>
        <Modal.Body className="text-center p-4">
          <div style={{ fontSize: 44, marginBottom: 10 }}>❌</div>
          <p style={{ fontWeight: 700, fontSize: 16, color: '#e53935', marginBottom: 8 }}>Loan Rejected</p>
          <p style={{ fontSize: 14, color: '#555' }}>
            This customer's loan application has been rejected. Please contact the branch for more details.
          </p>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <button className="btn btn-danger" onClick={() => setRejectModal(false)}>OK</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CustomerList;
